import {Button, Container, Flex, Space, Stack, Text, Title, Box, Paper, useMantineTheme} from "@mantine/core";

import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import { useMediaQuery } from '@mantine/hooks';
import {useLazyGetArticleDetailsQuery, useRefreshArticlesCacheMutation} from "../../../services/ArticleService";
import {ArticleDetails} from "../../../interfaces/response/ArticleDetails";
import {LoadingState} from "../../../interfaces/LoadingState";
import TableArticles from "../../../components/pageComponents/articoli/tableArticles/TableArticles";
import {TableArticleDetailsRow} from "../../../interfaces/tables/TableRows";
import ShellLayout from "../../../components/layoutComponents/shellLayout/ShellLayout";

import {DataTableColumn} from "mantine-datatable";
import { IconRefresh } from "@tabler/icons-react";

import CategoryColumn from "../../../components/pageComponents/articoli/tableArticles/categoryColumn/CategoryColumn";
import AvailabilityColumn
    from "../../../components/pageComponents/articoli/tableArticles/availabilityColumn/AvailabilityColumn";
import ActionColumn from "../../../components/pageComponents/articoli/tableArticles/actionColumn/ActionColumn";
import {mapArticleAttributes} from "../../../utils/ArticleUtils";
import ConnectionStatus from "../../../components/layoutComponents/connectionStatus/ConnectionStatus";

interface ArticlesContextType {
    fetchArticles: () => void;
    applyArticleUpdate: (updated: ArticleDetails) => void;
    applyArticles: (list: ArticleDetails[]) => void;
    getSaveStatus: (code: string) => LoadingState;
    setSaveStatus: (code: string, state: LoadingState) => void;
}

const ArticlesContext = createContext<ArticlesContextType>({
    fetchArticles: () => {},
    applyArticleUpdate: () => {},
    applyArticles: () => {},
    getSaveStatus: () => ({ loading: false, completed: false, error: false }),
    setSaveStatus: () => {}
});

type UseWrappedArticleColumns<T> = (columns: DataTableColumn<T>[]) => DataTableColumn<T>[];

const Articoli = () => {

    const [getArticleDetails, isLoadingGetArticleDetails, isErrorGetArticleDetaiils] = useLazyGetArticleDetailsQuery();
    const [refreshCache, {isLoading: refreshing}] = useRefreshArticlesCacheMutation();
    const [articles, setArticles] = useState<ArticleDetails[]>([]);
    // Persist per-SKU save statuses so the footer icon doesn't reset when rows rerender
    const [saveStatuses, setSaveStatuses] = useState<Record<string, LoadingState>>({});
    const [openedArticleModal, setOpenedArticleModal] = useState(false);
    const theme = useMantineTheme()
    const callback = useCallback

    // use media query to supply correct column set
    const isMobile = useMediaQuery('(max-width: 48em)');


    const mapArticleDetailsToRow = (article: ArticleDetails): TableArticleDetailsRow => {
        return {
            name: {
                image: article.headerArticleDetails.imageLink,
                title: article.headerArticleDetails.title
            },
            code: article.headerArticleDetails.code,
            ean: article.headerArticleDetails.ean,
            category: article.headerArticleDetails.category,
            grade: article.generalArticleDetails.grade,
            availability: article.headerArticleDetails.availability,
            details: article
                        }
    }

    const mappedArticles = articles.map(a => mapArticleDetailsToRow(a))

    const renderActionColumn = useCallback((article: TableArticleDetailsRow, index: number) => {
        //console.log("Rerendering Action Column")
        return (
            <ActionColumn article={article.details} />
        )
    }, [])

   const articleColumns: DataTableColumn<TableArticleDetailsRow>[] = useMemo(() => [
        // Mobile card layout
        {
            accessor: 'mobileCard',
            title: '',
            visibleMediaQuery: '(max-width: 48em)',
            render: (article: TableArticleDetailsRow) => (
                <Paper withBorder radius="md" p="sm" mb="sm" w="100%">
                <Flex gap="sm" align="center">
                    {/* image */}
                    <Flex align="center" justify="center" style={{minWidth: 75, minHeight: 75, width: 75}}>
                        <img style={{maxHeight: 75, maxWidth: 75, objectFit: 'cover'}} src={article.name.image as string} />
                    </Flex>
                    {/* right side */}
                    <Stack gap={6} style={{flex: 1}}>
                        {/* title */}
                        <Text size="xs" fw={600} style={{whiteSpace:'pre-wrap',wordBreak:'break-word',overflow:'hidden',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}}>{article.name.title}</Text>
                        {/* category + availability */}
                        <Flex gap={4} w="100%" wrap="wrap" mt={4} mb={4}>
                             <Box style={{flex:'0 0 48%', overflow:'hidden'}}>
                                 <CategoryColumn category={article.category} />
                             </Box>
                             <Box style={{flex:'0 0 48%'}}>
                                 <AvailabilityColumn availability={article.availability} mobile={isMobile} />
                             </Box>
                         </Flex>
                        {/* bottom section with sku info and actions */}
                        <Flex justify="space-between" align="center">
                            <Stack gap={2}>
                                <Text size="xs">{article.code}</Text>
                                <Text size="xs">{article.ean}</Text>
                                <Text size="xs">Grado: {article.grade}</Text>
                            </Stack>
                            <ActionColumn article={article.details} />
                        </Flex>
                    </Stack>
                </Flex>
                </Paper>
            )
        },
        // Image + name – always visible
        {
            accessor: 'name',
            visibleMediaQuery: '(min-width: 48em)',
            title: 'Articolo',
            width: '40%',
            render: (article: TableArticleDetailsRow, _index: number) => (
                <Flex gap="sm" align="center" direction={{ base: 'column', sm: 'row' }}>
                    <Flex align={'center'} justify={'center'} style={{minWidth: '75px', minHeight: '75px', width: '75px'}}>
                        <img style={{maxHeight: '75px', maxWidth: '75px', objectFit: 'cover'}}
                             src={article.name.image as string}/>
                    </Flex>
                    <Flex align={'center'}>
                        <Text
                            size={'xs'}
                            fw={500}
                            style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical'
                            }}>
                            {article.name.title}
                        </Text>
                    </Flex>
                </Flex>
            )
        },
        {
            accessor: 'category',
            title: 'Categoria',
            visibleMediaQuery: '(min-width: 48em)',
            width: '5%',
            noWrap: false,
            sortable: true,
            render: (article: TableArticleDetailsRow, _index: number) => (
                <CategoryColumn category={article.category} />
            )
        },
        {
            accessor: 'code',
            title: 'Codice',
            visibleMediaQuery: '(min-width: 48em)',
            width: '10%',
            sortable: true,
            render: (article: TableArticleDetailsRow, _index: number) => (
                <Text size={'xs'}> {article.code}</Text>
            )
        },
        {
            accessor: 'ean',
            title: "EAN",
            width: '10%',
            visibleMediaQuery: '(min-width: 48em)',
            sortable: true,
            render: (article: TableArticleDetailsRow, _index: number) => (
                <Text size={'xs'}> {article.ean}</Text>
            )
        },
       {
           accessor: 'grade',
           title: "Grado",
           width: '10%',
           visibleMediaQuery: '(min-width: 48em)',
           sortable: true,
           render: (article: TableArticleDetailsRow, _index: number) => (
               <Text size={'xs'}> {article.grade}</Text>
           )
       },
        {
            accessor: 'availability',
            visibleMediaQuery: '(min-width: 48em)',
            title: "Disponibilità",
            width: '5%',
            sortable: true,
            render: (article: TableArticleDetailsRow, _index: number) => (
                <AvailabilityColumn availability={article.availability} mobile={isMobile} />
            )
        },
        {
            accessor: 'details',
            title: 'Azioni',
            width: '5%',
            render: renderActionColumn
        }
    ], []);


    const columns = useMemo(
        () => (isMobile ? [articleColumns[0]] : articleColumns.slice(1)),
        [isMobile, articleColumns]
    );


    const fetchArticles = () => {
        getArticleDetails().then(resp => {
            if (resp.data === undefined) {
                // TODO Do something
            } else {
                setArticles(resp.data);
            }
        })
    }

    const applyArticleUpdate = useCallback((updated: ArticleDetails) => {
        setArticles(prev => {
            const idx = prev.findIndex(a => a.headerArticleDetails.code === updated.headerArticleDetails.code);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = updated;
                return copy;
            } else {
                return [updated, ...prev];
            }
        });
    }, []);

    const applyArticles = useCallback((list: ArticleDetails[]) => {
        setArticles(list ?? []);
    }, []);

    const getSaveStatus = useCallback((code: string) => {
        return saveStatuses[code] ?? { loading: false, completed: false, error: false };
    }, [saveStatuses]);

    const setSaveStatus = useCallback((code: string, state: LoadingState) => {
        setSaveStatuses(prev => ({ ...prev, [code]: state }));
    }, []);

    // Refresh button handler
    const handleRefresh = () => {
        refreshCache().then((resp: any) => {
            if (resp.data) {
                setArticles(resp.data);
            }
        })
    }

    useEffect(() => {
        fetchArticles();
    }, [])

    return (
        <ShellLayout
            Body={
                <>
                    <Space h={'lg'}></Space>
                    <Container size={'95%'}>
                        <Flex justify={'space-between'}>
                            <Title order={1} fw={500} c={theme.colors.textBlack[0]} mb={'xl'}>Articoli</Title>
                            <ConnectionStatus/>
                        </Flex>
                        <ArticlesContext.Provider value={{fetchArticles, applyArticleUpdate, applyArticles, getSaveStatus, setSaveStatus}}>
                            <TableArticles
                                columns={columns}
                                rows={mappedArticles}
                                loading={isLoadingGetArticleDetails.isFetching || refreshing}
                                onRefresh={handleRefresh}
                            />
                        </ArticlesContext.Provider>
                    </Container>
                </>
            }
        />
    )


}

export const useArticles = () => useContext(ArticlesContext);
export default Articoli;